import { supabase } from "../config/supabase";
import { getEventInvitationEmailTemplate } from "../templates/emails/eventInvitation";
import { getCoHostInvitationEmailTemplate } from "../templates/emails/coHostInvitation";
import { getEventUserInvitationTemplate } from '../templates/emails/eventUserInvitation';
import { getWaitingListNotificationTemplate } from '../templates/emails/waitingListNotification';
import { getInstantMessageEmailTemplate } from '../templates/emails/instantMessage';
import { v4 as uuidv4 } from 'uuid';
import { getEventHelperEmailTemplate } from "../templates/emails/eventHelping";

interface EventInvitationDetails {
  id: string;
  title: string;
  start_date: string;
  start_time: string;
  end_date?: string;
  end_time?: string;
  location?: string;
  type: string;
  category: string;
  modality: string;
  host_name: string;
  host_id: string;
  image_url?: string;
  base_url: string;
}

interface EventManagerInvitationDetails extends Omit<EventInvitationDetails, 'host_id'> {
  manager_role?: 'admin' | 'coordinator' | 'assistant';
}

interface InstantMessageDetails {
  subject: string;
  message: string;
  sender_name: string;
  image_url: string;
  sender_email: string;
  event_title?: string;
  event_id?: string;
}

export class EmailService {
  /**
   * Send email using Supabase Edge Function
   */
  private static async sendEmail(
    to: string,
    subject: string,
    html: string,
    metadata?: Record<string, any>
  ): Promise<{ error: any }> {
    try {
      console.log('=== EmailService.sendEmail ===');
      console.log('To:', to);
      console.log('Subject:', subject);
      console.log('HTML length:', html?.length);
      console.log('Metadata:', metadata);
      
      // Verify supabase client
      
      
      const { data, error } = await supabase.functions.invoke('hyper-responder', {
        body: {
          to,
          subject,
          html,
          metadata
        }
      });

      console.log('Raw Supabase response:', { data, error });
      console.log('Error type:', typeof error);
      console.log('Data type:', typeof data);

      if (error) {
        console.error('Supabase function error details:', {
          message: error.message,
          status: error.status,
          statusText: error.statusText,
          context: error.context,
          details: error.details
        });
        return { error };
      }

      console.log('Email sent successfully:', data);
      return { error: null };
    } catch (error: any) {
      console.error('Exception in sendEmail:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      return { error };
    }
  }



  /**
   * Send an invitation email for an event co-host.
   */
  static async sendEventInvitation(
    recipientEmail: string, 
    eventDetails: EventInvitationDetails
  ): Promise<{ error: any }> {
    if (!eventDetails.base_url) {
      eventDetails.base_url = process.env.NEXT_PUBLIC_APP_URL || 'https://yourdomain.com';
    }
    
    const { subject, body } = getEventInvitationEmailTemplate(eventDetails);

    return await this.sendEmail(
      recipientEmail,
      subject,
      body,
      { 
        event_id: eventDetails.id, 
        host_id: eventDetails.host_id,
        type: 'co_host_invitation'
      }
    );
  }

  /**
   * Send an invitation email for an event manager.
   */
  static async sendEventAttendanceInvitation(
    recipientEmail: string, 
    eventDetails: EventManagerInvitationDetails
  ): Promise<{ error: any, managerToken?: string }> {
    try {
      if (!eventDetails.base_url) {
        eventDetails.base_url = process.env.NEXT_PUBLIC_APP_URL || 'https://yourdomain.com';
      }
      
      // Generate a unique token for this invitation
      const managerToken = uuidv4();
      
      // Prepare the template data with the token
      const templateData = {
        ...eventDetails,
        manager_token: managerToken
      };
      
      const { subject, body } = getEventHelperEmailTemplate(templateData);

      // Send email directly
      const { error: emailError } = await this.sendEmail(
        recipientEmail,
        subject,
        body,
        { 
          event_id: eventDetails.id,
          manager_token: managerToken,
          manager_role: eventDetails.manager_role || 'coordinator',
          type: 'event_manager_invitation'
        }
      );

      if (emailError) {
        return { error: emailError };
      }

      // Store the token for verification
      const { error: tokenError } = await supabase
        .from('invitation_tokens')
        .insert([
          {
            token: managerToken,
            email: recipientEmail,
            event_id: eventDetails.id,
            role: eventDetails.manager_role || 'coordinator',
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'pending'
          }
        ]);

      return { error: tokenError, managerToken };
    } catch (error) {
      console.error("Error sending event manager invitation:", error);
      return { error };
    }
  }
  
  static async verifyManagerToken(token: string, eventId: string): Promise<{ 
    isValid: boolean, 
    email?: string, 
    role?: string,
    error?: any 
  }> {
    try {
      const { data, error } = await supabase
        .from('invitation_tokens')
        .select('*')
        .eq('token', token)
        .eq('event_id', eventId)
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        return { isValid: false, error: error || 'Invalid or expired token' };
      }

      return { 
        isValid: true, 
        email: data.email,
        role: data.role 
      };
    } catch (error) {
      return { isValid: false, error };
    }
  }

  static async markTokenAsUsed(token: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('invitation_tokens')
        .update({ 
          status: 'used', 
          used_at: new Date().toISOString() 
        })
        .eq('token', token);

      return { error };
    } catch (error) {
      return { error };
    }
  }

  /**
   * Generate a token for co-host invitation
   */
  static async generateCoHostToken(
    eventId: string,
    email: string,
    name: string
  ): Promise<{ data: { token: string }, error: any }> {
    try {
      const token = uuidv4();
      
      const { error } = await supabase
        .from('invitation_tokens')
        .insert([
          {
            token,
            email,
            event_id: eventId,
            role: 'cohost',
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'pending',
            metadata: { name }
          }
        ]);

      if (error) {
        return { data: { token: '' }, error };
      }

      return { data: { token }, error: null };
    } catch (error) {
      return { data: { token: '' }, error };
    }
  }

  /**
   * Send co-host invitation email
   */
  static async sendCoHostInvitation(
    eventId: string,
    email: string,
    name: string,
    token: string
  ): Promise<{ error: any }> {
    try {
      // Get event details for the email
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('title, start_date, start_time, location, image_url')
        .eq('id', eventId)
        .single();

      if (eventError || !event) {
        return { error: eventError || 'Event not found' };
      }

      const baseUrl = 'https://cap.com';
      const invitationUrl = `${baseUrl}/events/${eventId}/cohost?token=${token}`;

      const { subject, body } = getCoHostInvitationEmailTemplate({
        eventTitle: event.title,
        eventStartDate: event.start_date,
        eventStartTime: event.start_time,
        eventLocation: event.location,
        eventImageUrl: event.image_url,
        coHostName: name,
        invitationUrl: invitationUrl,
      });

      return await this.sendEmail(
        email,
        subject,
        body,
        { 
          event_id: eventId,
          token,
          type: 'cohost_invitation'
        }
      );
    } catch (error) {
      return { error };
    }
  }

  /**
   * Verify co-host token
   */
  static async verifyCoHostToken(token: string, eventId: string): Promise<{ 
    isValid: boolean, 
    email?: string, 
    name?: string,
    error?: any 
  }> {
    try {
      const { data, error } = await supabase
        .from('invitation_tokens')
        .select('*')
        .eq('token', token)
        .eq('event_id', eventId)
        .eq('role', 'cohost')
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        return { isValid: false, error: error || 'Invalid or expired token' };
      }

      return { 
        isValid: true, 
        email: data.email,
        name: data.metadata?.name || data.email.split('@')[0]
      };
    } catch (error) {
      return { isValid: false, error };
    }
  }

  /**
   * Generate invitation token for event user
   */
  static async generateEventUserToken(
    eventId: string,
    email: string,
    name: string
  ): Promise<{ data: { token: string } | null; error: any }> {
    try {
      const token = uuidv4();
      
      // Almacenar token en la tabla de tokens
      const { error } = await supabase
        .from('invitation_tokens')
        .insert([{
          token,
          email,
          event_id: eventId,
          role: 'event_user',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'pending',
          metadata: { name }
        }]);

      if (error) {
        return { data: null, error };
      }

      return { 
        data: { token }, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Failed to generate token' }
      };
    }
  }

  /**
   * Send event user invitation with token - ACTUALIZADO
   */
  static async sendEventUserInvitation(
    recipientEmail: string,
    eventDetails: {
      id: string;
      title: string;
      start_date: string;
      start_time: string;
      location?: string;
      host_name: string;
      invitation_token: string;
      is_on_waiting_list?: boolean;
      waiting_list_position?: number;
      custom_message?: string;
    }
  ): Promise<{ error: any }> {
    const { subject, body } = getEventUserInvitationTemplate({
      ...eventDetails,
      base_url: 'https://cap.com'
    });

    return this.sendEmail(recipientEmail, subject, body, {
      type: 'event_user_invitation',
      event_id: eventDetails.id,
      invitation_token: eventDetails.invitation_token
    });
  }

  /**
   * Send waiting list notification - ACTUALIZADO
   */
  static async sendWaitingListNotification(
    recipientEmail: string,
    eventDetails: {
      id: string;
      title: string;
      start_date: string;
      start_time: string;
      location?: string;
    }
  ): Promise<{ error: any }> {
    const { subject, body } = getWaitingListNotificationTemplate({
      ...eventDetails,
      base_url: 'https://cap.com'
    });

    return this.sendEmail(recipientEmail, subject, body, {
      type: 'waiting_list_notification',
      event_id: eventDetails.id
    });
  }

  /**
   * Verify event user token
   */
  static async verifyEventUserToken(token: string, eventId: string): Promise<{ 
    isValid: boolean, 
    email?: string, 
    name?: string,
    error?: any 
  }> {
    try {
      const { data, error } = await supabase
        .from('invitation_tokens')
        .select('*')
        .eq('token', token)
        .eq('event_id', eventId)
        .eq('role', 'event_user')
        .eq('status', 'pending')
        .gte('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        return { isValid: false, error: error || 'Invalid or expired token' };
      }

      return { 
        isValid: true, 
        email: data.email,
        name: data.metadata?.name || data.email.split('@')[0]
      };
    } catch (error) {
      return { isValid: false, error };
    }
  }

  /**
   * Send instant message email
   */
  static async sendInstantMessage(
    recipientEmail: string,
    messageDetails: InstantMessageDetails
  ): Promise<{ error: any }> {
    try {
      const templateData = {
        ...messageDetails,
        base_url: 'https://cap.com'
      };

      const { subject, body } = getInstantMessageEmailTemplate(templateData);

      return await this.sendEmail(
        recipientEmail,
        subject,
        body,
        {
          type: 'instant_message',
          sender_email: messageDetails.sender_email,
          event_id: messageDetails.event_id
        }
      );
    } catch (error) {
      console.error('Error sending instant message:', error);
      return { error };
    }
  }

  /**
   * Send instant message to all confirmed users of an event
   */
  static async sendInstantMessageToEventUsers(
    eventId: string,
    messageDetails: Omit<InstantMessageDetails, 'event_id'> & { event_id: string }
  ): Promise<{ error: any, sent_count?: number }> {
    try {
      // Obtener todos los usuarios confirmados del evento
      const { data: confirmedUsers, error: usersError } = await supabase
        .from('event_users')
        .select('email, name')
        .eq('event_id', eventId)
        .eq('approval_status', 'approved');

      if (usersError) {
        console.error('Error fetching confirmed users:', usersError);
        return { error: usersError };
      }

      if (!confirmedUsers || confirmedUsers.length === 0) {
        return { error: 'No confirmed users found for this event' };
      }

      const templateData = {
        ...messageDetails,
        base_url: 'https://cap.com'
      };

      const { subject, body } = getInstantMessageEmailTemplate(templateData);

      // Enviar email a cada usuario confirmado
      const emailPromises = confirmedUsers.map(user => 
        this.sendEmail(
          user.email,
          subject,
          body,
          {
            type: 'instant_message_broadcast',
            sender_email: messageDetails.sender_email,
            event_id: messageDetails.event_id,
            recipient_name: user.name
          }
        )
      );

      const results = await Promise.allSettled(emailPromises);
      
      // Contar cuántos emails se enviaron exitosamente
      const successCount = results.filter(result => 
        result.status === 'fulfilled' && !result.value.error
      ).length;

      if (successCount === 0) {
        return { error: 'Failed to send messages to any recipients' };
      }

      return { error: null, sent_count: successCount };

    } catch (error) {
      console.error('Error sending instant message to event users:', error);
      return { error };
    }
  }
}